import React, { useEffect, useState } from 'react';
import '../../App.css';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { useNavigate } from 'react-router-dom';
import i18n from 'i18next';
import {
  Box,
  Text,
  Menu,
  Image,
  Container,
  VStack,
  Flex,
  Heading,
  Spacer,
  Tabs,
  Tab,
  TabList,
  Stack,
  SimpleGrid,
} from '@chakra-ui/react';
import useDetectKeyboardOpen from 'use-detect-keyboard-open';
import { isMobile, isDesktop } from 'react-device-detect';

import { LandingScreen } from './landingScreen';
import {
  CONTRACT_CUSTOMER,
  FINDER_SCREEN_PATH,
  TERMS_SCREEN_PATH,
} from '../../support/routeConstants';
import BtInput from '../../components/BtInput';
import { BtNavigate, BtNavigateFotter } from '../../components/BtNavigate';
import { FinderScreen } from '../finder/finderScreen';
import BtLoading from '../../components/BtLoading';
import BtFailed from '../../components/BtFailed';
import BtButton from '../../components/BtButton';
import { images } from '../../support/imageSupport';
import { SearchScreen } from '../search/searchScreen';
import { BookScreen } from '../book/bookScreen';
import { MyBookingScreen } from '../myBooking/myBookingScreen';
import { Support } from '../support/support';
import { SupportScreen } from '../support/supportScreen';
import BtHelmet from '../../components/BtHelmet';
import { ContractCustomerScreen } from '../contractCustomer/contractCustomerScreen';
import { fetchCompanyInformation } from '../gat/gatSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { mapBackendLocaleToI18n } from '../../support/helpers';

export const Landing: React.FC = () => {
  const viewModel = useAppSelector((state) => state.landing.viewModel);
  const logoUrl = useSelector((state: RootState) => state.gatInformation.logoUrl);
  const locale = useSelector((state: RootState) => state.gatInformation.locale);
  const companyInfoStatus = useSelector((state: RootState) => state.gatInformation.status);
  const viewModelFinder = useAppSelector((state) => state.finder.viewModel);
  const viewModelSupport = useAppSelector((state) => state.support.viewModel);
  const isKeyboardOpen = useDetectKeyboardOpen();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [state, setState] = useState({
    govId: viewModel.state.govId,
    regno: viewModel.state.regno,
    weight: viewModel.state.weight,
    customerNumber: viewModel.state.customerNumber,
    govNo: viewModel.state.govNo,
  });

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const target = event.target;
    const value = target.value.toUpperCase();

    setState({ ...state, [target.name]: value });
  };

  const handleOnFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    return document.getElementById(event.target.id)?.focus();
  };

  const handleRegister = async (registerd: boolean) => {
    dispatch(await LandingScreen.registration(viewModel, registerd, logoUrl));
  };

  const handleLogin = async () => {
    dispatch(await LandingScreen.login(viewModel, { ...state }, logoUrl));
  };

  const handleCustomer = async (type: string) => {
    if (type === 'person') {
      dispatch(await LandingScreen.changeToPerson(viewModel, logoUrl));
    } else {
      dispatch(await LandingScreen.changeToCompany(viewModel, logoUrl));
    }
  };

  const saveState = async (isActive?: LandingScreen.ActiveViewModel) => {
    dispatch(await LandingScreen.save(viewModel, logoUrl, { ...state }, isActive));
    handleNavigate(TERMS_SCREEN_PATH, isActive);
  };

  const handleNavigate = async (path: string, isActive?: LandingScreen.ActiveViewModel) => {
    dispatch(await LandingScreen.save(viewModel, logoUrl, { ...state }, isActive));
    if (path !== '') {
      navigate(path);
    } else {
      dispatch(SupportScreen.start(viewModelSupport));
    }
  };

  const resetEverything = async () => {
    dispatch(await SearchScreen.reset(logoUrl));
    dispatch(await BookScreen.reset());
    dispatch(await FinderScreen.reset());
    dispatch(await MyBookingScreen.reset(logoUrl));
    dispatch(await ContractCustomerScreen.reset());
  };

  useEffect(() => {
    if (companyInfoStatus !== 'succeeded') return;

    i18n.changeLanguage(mapBackendLocaleToI18n(locale));

    if (LandingScreen.isNone(viewModel)) {
      dispatch(LandingScreen.start(viewModel, logoUrl));
      resetEverything();
    } else if (LandingScreen.isLoading(viewModel)) {
      (async () => {
        dispatch(await LandingScreen.load(viewModel, logoUrl));
      })();
    } else if (LandingScreen.isFinished(viewModel)) {
      navigate(FINDER_SCREEN_PATH);
      dispatch(FinderScreen.start(viewModelFinder, logoUrl));
    }
  }, [viewModel, companyInfoStatus, locale]);

  useEffect(() => {
    dispatch(fetchCompanyInformation());
  }, [dispatch]);

  if (LandingScreen.isLoading(viewModel)) {
    return <BtLoading {...viewModel.loading} />;
  } else if (LandingScreen.isPerson(viewModel)) {
    return (
      <>
        <BtHelmet title={viewModel.metaTitle} />
        <Container padding={0} size='xl'>
          <BtNavigate {...viewModel.navigationTop} onClick={() => {}} />
          <Container padding={0} maxW='container.md'>
            <Flex pb={200}>
              <VStack w='full' h='full' p={10}>
                <Image pb={2} src={images(viewModel.image)} />
                <Heading pb={3} as='h1' variant='h1'>
                  {viewModel.greeting}
                </Heading>
                <Heading pb={7} as='h4' variant='h4' textAlign={'center'}>
                  {viewModel.body}
                </Heading>
                <Box bg='darkgray.100' borderRadius='full'>
                  <Tabs
                    index={0}
                    variant='soft-rounded'
                    colorScheme='brand'
                    onChange={() => handleCustomer('company')}
                  >
                    <TabList>
                      <Tab
                        _selected={{ color: 'black', bg: 'brand.500' }}
                        color='white'
                        _focus={{ ringColor: 'brand.500' }}
                      >
                        {viewModel.private}
                      </Tab>
                      <Tab
                        _selected={{ color: 'black', bg: 'brand.500' }}
                        color='white'
                        _focus={{ ringColor: 'brand.500' }}
                      >
                        {viewModel.company}
                      </Tab>
                    </TabList>
                  </Tabs>
                </Box>
                <Stack
                  w='full'
                  pt={5}
                  spacing={[2, 8]}
                  direction={['column', 'row']}
                  id={'ScrollToMePerson'}
                >
                  <BtInput
                    {...viewModel.regno}
                    value={state.regno}
                    onChange={handleOnChange}
                    onFocus={handleOnFocus}
                  />
                  <BtInput
                    {...viewModel.govId}
                    value={state.govId}
                    onChange={handleOnChange}
                    onFocus={handleOnFocus}
                  />
                </Stack>

                <Flex w='full'>
                  <Text pr='2'>{viewModel.registration}</Text>
                  <BtButton
                    variant='link'
                    {...viewModel.open}
                    action={() => handleRegister(true)}
                  />
                  <Spacer />
                </Flex>

                {viewModel.state.registered && (
                  <Box w='100%' p={4} pb={5} bg='black' borderRadius='lg'>
                    <Flex>
                      <Spacer />
                      <BtButton
                        variant='ghost'
                        {...viewModel.close}
                        action={() => handleRegister(false)}
                      />
                    </Flex>
                    <BtInput
                      {...viewModel.weight}
                      value={state.weight}
                      onChange={handleOnChange}
                      onFocus={handleOnFocus}
                    />
                  </Box>
                )}
                <Flex pb={4}></Flex>
                <BtButton
                  variant='primary'
                  size='lg'
                  w='full'
                  {...viewModel.next}
                  action={() => handleLogin()}
                />
                <BtButton
                  variant='terms'
                  w='full'
                  {...viewModel.terms}
                  p={6}
                  action={() => saveState()}
                />
              </VStack>
            </Flex>
          </Container>
          {!isKeyboardOpen && isMobile && !isDesktop && (
            <>
              {/* <Box
                pos='fixed'
                bottom='114px'
                h={['160px', '120px', '120px']}
                w='full'
                p={10}
                bg='black'
                borderTop='1px solid rgba(82, 75, 66)'
                borderBottom='1px solid rgba(82, 75, 66)'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <Stack spacing={[2, 5]} direction={['column', 'row']} align='center'>
                  <Stack spacing={5} direction={['row', 'row']} align='center'>
                    <Image pb={2} src={images(viewModel.uspImage)} />
                    <Text pr='2'>{viewModel.customerUsp}</Text>
                  </Stack>
                  <BtButton
                    variant='primary'
                    size='lg'
                    w='full'
                    {...viewModel.customerUspBtn}
                    action={() => handleNavigate(CONTRACT_CUSTOMER, 'company')}
                  />
                </Stack>
              </Box>*/}

              <Box
                pos='fixed'
                bottom='0'
                w='100%'
                h='114px'
                bg='black'
                zIndex={2}
                display='flex'
                alignItems='center'
              >
                <Spacer />
                <Menu>
                  {viewModel.navigation.map((item) => {
                    return <BtNavigateFotter key={item.title} {...item} onClick={handleNavigate} />;
                  })}
                </Menu>
                <Spacer />
              </Box>
            </>
          )}

          {isDesktop && (
            <>
              {/*   <Box
                pos='fixed'
                bottom='114px'
                h={['160px', '120px', '120px']}
                w='full'
                p={10}
                bg='black'
                borderTop='1px solid rgba(82, 75, 66)'
                borderBottom='1px solid rgba(82, 75, 66)'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <Stack spacing={[2, 5]} direction={['column', 'row']} align='center'>
                  <Stack spacing={5} direction={['row', 'row']} align='center'>
                    <Image pb={2} src={images(viewModel.uspImage)} />
                    <Text pr='2'>{viewModel.customerUsp}</Text>
                  </Stack>
                  <BtButton
                    variant='primary'
                    size='lg'
                    w='full'
                    {...viewModel.customerUspBtn}
                    action={() => handleNavigate(CONTRACT_CUSTOMER, 'company')}
                  />
                </Stack>
              </Box>
*/}
              <Box
                pos='fixed'
                bottom='0'
                w='100%'
                h='114px'
                bg='black'
                zIndex={2}
                display='flex'
                alignItems='center'
              >
                <Spacer />
                <Menu>
                  {viewModel.navigation.map((item) => {
                    return <BtNavigateFotter key={item.title} {...item} onClick={handleNavigate} />;
                  })}
                </Menu>
                <Spacer />
              </Box>
            </>
          )}

          {(SupportScreen.isLoading(viewModelSupport) ||
            SupportScreen.isReady(viewModelSupport)) && <Support />}
        </Container>
      </>
    );
  } else if (LandingScreen.isCompany(viewModel)) {
    return (
      <>
        <BtHelmet title={viewModel.metaTitle} />
        <Container padding={0} size='xl'>
          <BtNavigate {...viewModel.navigationTop} onClick={() => {}} />
          <Container padding={0} maxW='container.md'>
            <Flex pb={200}>
              <VStack w='full' h='full' p={10}>
                <Image pb={2} src={images(viewModel.image)} />
                <Heading pb={3} as='h1' variant='h1'>
                  {viewModel.greeting}
                </Heading>
                <Heading pb={7} as='h4' variant='h4' textAlign={'center'}>
                  {viewModel.body}
                </Heading>
                <Box bg='darkgray.100' borderRadius='full'>
                  <Tabs
                    index={1}
                    variant='soft-rounded'
                    colorScheme='brand'
                    onChange={() => handleCustomer('person')}
                  >
                    <TabList>
                      <Tab
                        _selected={{ color: 'black', bg: 'brand.500' }}
                        color='white'
                        _focus={{ ringColor: 'brand.500' }}
                      >
                        {viewModel.private}
                      </Tab>
                      <Tab
                        _selected={{ color: 'black', bg: 'brand.500' }}
                        color='white'
                        _focus={{ ringColor: 'brand.500' }}
                      >
                        {viewModel.company}
                      </Tab>
                    </TabList>
                  </Tabs>
                </Box>
                <SimpleGrid
                  w='full'
                  columns={[1, 2]}
                  pt={5}
                  pb={5}
                  spacing={[5, 8]}
                  direction={['row', 'column']}
                  id={'ScrollToMeCompany'}
                >
                  <SimpleGrid
                    w='full'
                    columns={[1]}
                    spacing={[2, 2]}
                    direction={['column', 'column']}
                  >
                    <BtInput
                      {...viewModel.regno}
                      value={state.regno}
                      onChange={handleOnChange}
                      onFocus={handleOnFocus}
                    />

                    <Flex w='full' alignItems={'start'} flexDirection={['row', 'column', 'row']}>
                      <Text pr='2'>{viewModel.registration}</Text>
                      <BtButton
                        variant='link'
                        p={0.5}
                        {...viewModel.open}
                        action={() => handleRegister(true)}
                      />
                      <Spacer />
                    </Flex>
                  </SimpleGrid>
                  <SimpleGrid w='full' columns={[1]} spacing={[2]} direction={['column', 'column']}>
                    <BtInput
                      {...viewModel.govNo}
                      value={state.govNo}
                      onChange={handleOnChange}
                      onFocus={handleOnFocus}
                    />
                  </SimpleGrid>
                  <SimpleGrid w='full' columns={[1]} spacing={[2]} direction={['column', 'column']}>
                    {/* <BtInput
                      {...viewModel.customerNumber}
                      value={state.customerNumber}
                      onChange={handleOnChange}
                      onFocus={handleOnFocus}
                    />*/}
                  </SimpleGrid>
                </SimpleGrid>
                {viewModel.state.registered && (
                  <Box w='100%' p={4} bg='black' borderRadius='lg' pb={4}>
                    <Flex>
                      <Spacer />
                      <BtButton
                        variant='ghost'
                        size=''
                        w=''
                        {...viewModel.close}
                        action={() => handleRegister(false)}
                      />
                    </Flex>
                    <BtInput
                      {...viewModel.weight}
                      value={state.weight}
                      onChange={handleOnChange}
                      onFocus={handleOnFocus}
                    />
                  </Box>
                )}
                <BtButton
                  variant='primary'
                  size='lg'
                  w='full'
                  {...viewModel.next}
                  action={() => handleLogin()}
                />
                <BtButton
                  variant='terms'
                  w='full'
                  {...viewModel.terms}
                  p={6}
                  action={() => saveState()}
                />
              </VStack>
            </Flex>
          </Container>
          {!isKeyboardOpen && isMobile && !isDesktop && (
            <>
              <Box
                pos='fixed'
                bottom='114px'
                h={['160px', '120px', '120px']}
                w='full'
                p={10}
                bg='black'
                borderTop='1px solid rgba(82, 75, 66)'
                borderBottom='1px solid rgba(82, 75, 66)'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <Stack spacing={[2, 5]} direction={['column', 'row']} align='center'>
                  <Stack spacing={5} direction={['row', 'row']} align='center'>
                    <Image pb={2} src={images(viewModel.uspImage)} />
                    <Text pr='2'>{viewModel.customerUsp}</Text>
                  </Stack>
                  <BtButton
                    variant='primary'
                    size='lg'
                    w='full'
                    {...viewModel.customerUspBtn}
                    action={() => handleNavigate(CONTRACT_CUSTOMER, 'company')}
                  />
                </Stack>
              </Box>
              <Box
                pos='fixed'
                bottom='0'
                w='100%'
                h='114px'
                bg='black'
                zIndex={2}
                display='flex'
                alignItems='center'
              >
                <Spacer />
                <Menu>
                  {viewModel.navigation.map((item) => {
                    return <BtNavigateFotter key={item.title} {...item} onClick={handleNavigate} />;
                  })}
                </Menu>
                <Spacer />
              </Box>
            </>
          )}
          {isDesktop && (
            <>
              {/*   <Box
                pos='fixed'
                bottom='114px'
                h={['160px', '120px', '120px']}
                w='full'
                p={10}
                bg='black'
                borderTop='1px solid rgba(82, 75, 66)'
                borderBottom='1px solid rgba(82, 75, 66)'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <Stack spacing={[2, 5]} direction={['column', 'row']} align='center'>
                  <Stack spacing={5} direction={['row', 'row']} align='center'>
                    <Image pb={2} src={images(viewModel.uspImage)} />
                    <Text pr='2'>{viewModel.customerUsp}</Text>
                  </Stack>
                  <BtButton
                    variant='primary'
                    size='lg'
                    w='full'
                    {...viewModel.customerUspBtn}
                    action={() => handleNavigate(CONTRACT_CUSTOMER, 'company')}
                  />
                </Stack>
              </Box>*/}
              <Box
                pos='fixed'
                bottom='0'
                w='100%'
                h='114px'
                bg='black'
                zIndex={2}
                display='flex'
                alignItems='center'
              >
                <Spacer />
                <Menu>
                  {viewModel.navigation.map((item) => {
                    return <BtNavigateFotter key={item.title} {...item} onClick={handleNavigate} />;
                  })}
                </Menu>
                <Spacer />
              </Box>
            </>
          )}
          {(SupportScreen.isLoading(viewModelSupport) ||
            SupportScreen.isReady(viewModelSupport)) && <Support />}
        </Container>
      </>
    );
  } else if (LandingScreen.isFailed(viewModel)) {
    return <BtFailed />;
  } else {
    return <BtLoading {...viewModel.loading} />;
  }
};
